import React from 'react';
import { useTranslation } from 'react-i18next';

interface PartnershipProps {
  onPartnerClick: () => void;
}

/**
 * The FDE partnership model: train on the customer's data, deploy on their
 * servers, hand the system off to their team, stay on call for support.
 * Sits after Convergence; needs no scene anchor (the sky is in its dusk tail
 * and the re-merged cloud accompanies the section at the horizon).
 */
const STEPS = ['train', 'deploy', 'handoff', 'support'] as const;

const Partnership: React.FC<PartnershipProps> = ({ onPartnerClick }) => {
  const { t } = useTranslation();

  return (
    <section id="lab-partnership" className="relative z-10 px-4 pb-12 pt-24 md:px-[72px]">
      <div className="mx-auto w-full max-w-[1280px]">
        <div className="mx-auto max-w-[720px] text-center [text-shadow:0_2px_30px_rgba(0,0,0,0.5)]">
          <div className="font-plexmono text-[11.5px] tracking-[0.18em] text-lab-asr">
            {t('lab.partnership.kicker')}
          </div>
          <h2 className="mt-5 text-[clamp(28px,5vw,44px)] font-semibold leading-[1.1] tracking-[-0.02em] [text-wrap:balance]">
            {t('lab.partnership.title')}
          </h2>
          <p className="mx-auto mt-5 max-w-[620px] text-[15px] leading-[1.7] text-lab-frost/70 md:text-base">
            {t('lab.partnership.desc')}
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step, i) => (
            <div key={step} className="lab-glass lab-siri rounded-[14px] p-6">
              <div className="font-plexmono text-[11px] tracking-[0.12em] text-lab-asr">
                {String(i + 1).padStart(2, '0')}
              </div>
              <div className="mt-2.5 text-[19px] font-semibold tracking-[-0.01em]">
                {t(`lab.partnership.steps.${step}.title`)}
              </div>
              <p className="mt-2 text-[14px] leading-[1.65] text-lab-frost/70">
                {t(`lab.partnership.steps.${step}.desc`)}
              </p>
            </div>
          ))}
        </div>
        {/* Objection-killer hook: the unspoken blocker for on-prem AI is "we
            don't have the people for this" — answered here, right at the CTA */}
        <div className="mt-12 text-center [text-shadow:0_2px_30px_rgba(0,0,0,0.5)]">
          <div className="text-[19px] font-semibold tracking-[-0.01em] md:text-[22px]">
            {t('lab.partnership.hookTitle')}
          </div>
          <p className="mx-auto mt-2 max-w-[560px] text-[14.5px] leading-[1.65] text-lab-frost/70">
            {t('lab.partnership.hookDesc')}
          </p>
          <button
            onClick={onPartnerClick}
            className="lab-siri mt-6 inline-block rounded-full bg-lab-frost px-[34px] py-[15px] text-[15px] font-semibold text-lab-ink transition-transform hover:-translate-y-0.5"
          >
            {t('lab.partnership.cta')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Partnership;
